'use client';

import React, { useEffect, useState } from 'react';

interface MockPreviewProps {
    isActive: boolean;
    onComplete: () => void;
}

export function MockPreview({ isActive, onComplete }: MockPreviewProps) {
    const [cursorPos, setCursorPos] = useState({ x: '50%', y: '50%' });
    const [showCursor, setShowCursor] = useState(false);

    useEffect(() => {
        if (isActive) {
            setShowCursor(true);
            animateCursor();
        } else {
            setShowCursor(false);
        }
    }, [isActive]);

    const animateCursor = async () => {
        // Start position
        setCursorPos({ x: '50%', y: '50%' });
        await wait(500);

        // Move to publish button (Top rightish)
        setCursorPos({ x: '90%', y: '10%' });
        await wait(1000); // Travel time

        // Simulate Click
        const btn = document.getElementById('mock-publish-btn');
        if (btn) btn.style.transform = "scale(0.95)";
        await wait(150);
        if (btn) btn.style.transform = "scale(1)";
        await wait(300);

        onComplete();
    };

    const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    return (
        <div className="w-full h-full bg-white flex flex-col relative">
            {/* Mouse Cursor */}
            <div
                className="absolute z-50 pointer-events-none transition-all duration-1000 ease-in-out"
                style={{
                    left: cursorPos.x,
                    top: cursorPos.y,
                    opacity: showCursor ? 1 : 0
                }}
            >
                <svg className="w-8 h-8 text-black drop-shadow-lg" fill="currentColor" viewBox="0 0 24 24"><path d="M5.5 3.21l.01 17.5 4.67-4.85 3.8 8.87 2.94-1.25-3.8-8.87 8.16-.03L5.5 3.21z" stroke="white" strokeWidth="1.5" /></svg>
            </div>

            {/* Header */}
            <div className="h-auto md:h-14 border-b flex flex-col md:flex-row items-center justify-between px-4 py-3 md:py-0 bg-white z-10 relative gap-3 md:gap-0">
                <div className="flex items-center gap-3 md:gap-4 text-sm text-gray-500 w-full md:w-auto justify-between md:justify-start">
                    <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
                        <div className="p-1 bg-white shadow rounded"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg></div>
                        <div className="p-1"><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg></div>
                    </div>
                    <div className="h-4 w-px bg-gray-300 hidden md:block"></div>
                    <span className="hidden md:inline">Home</span>
                    <span className="md:hidden text-xs bg-gray-100 px-2 py-1 rounded">Waitlistly Builder</span>
                </div>
                <div className="flex items-center gap-2 md:gap-3 w-full md:w-auto">
                    <button className="flex-1 md:flex-none px-3 py-2 text-xs md:text-sm font-medium text-gray-700 border rounded-lg whitespace-nowrap bg-white hover:bg-gray-50">Edit Content</button>
                    <button id="mock-publish-btn" className="flex-1 md:flex-none px-4 py-2 text-xs md:text-sm font-medium text-white bg-indigo-600 rounded-lg shadow-sm transition-transform shadow-indigo-200">Publish Site</button>
                </div>
            </div>

            {/* Canvas / Iframe */}
            <div className="flex-1 overflow-y-auto bg-gray-100 p-8 flex justify-center">
                <div className="w-full max-w-4xl bg-white shadow-xl rounded-lg overflow-hidden min-h-[600px] origin-top transform scale-95 opacity-100 animate-in zoom-in-95 duration-500">
                    {/* Hero Section of generated site */}
                    <div className="p-12 text-center bg-gradient-to-br from-indigo-50 to-white">
                        <span className="inline-block px-3 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-semibold tracking-wide uppercase mb-6">Beta Access</span>
                        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">Validate Your Startup Idea<br />in Days, Not Months</h1>
                        <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">Entrepreneurs use Waitlistly to capture real interest, test market fit, and decide before writing code.</p>

                        <div className="flex justify-center gap-4">
                            <button className="px-8 py-3 bg-indigo-600 text-white rounded-full font-medium shadow-lg hover:bg-indigo-700">Join the Waitlist</button>
                            <button className="px-8 py-3 bg-white text-gray-700 border border-gray-200 rounded-full font-medium hover:bg-gray-50">Learn More</button>
                        </div>
                    </div>

                    {/* Features grid placeholder */}
                    <div className="grid grid-cols-3 gap-8 p-12 bg-white">
                        {[1, 2, 3].map(i => (
                            <div key={i} className="space-y-3">
                                <div className="w-12 h-12 bg-indigo-50 rounded-lg"></div>
                                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-3 bg-gray-100 rounded w-full"></div>
                                <div className="h-3 bg-gray-100 rounded w-5/6"></div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
